import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface PageSiteContextValue {
  /** The site ID from the current page context (e.g., from URL params) */
  pageSiteId: string | null;
  /** The site name from the current page context */
  pageSiteName: string | null;
  /** Set the page site context - call this from pages that have a specific site ID */
  setPageSiteContext: (siteId: string | null, siteName?: string | null) => void;
  /** Clear the page site context */
  clearPageSiteContext: () => void;
}

const PageSiteContext = createContext<PageSiteContextValue | undefined>(undefined);

interface PageSiteProviderProps {
  children: ReactNode;
}

/**
 * Provider for page-level site context.
 * 
 * This allows pages like ClientSetupDashboard to set the current site context
 * so that global components like NotificationCenter can filter by that site.
 */
export function PageSiteProvider({ children }: PageSiteProviderProps) {
  const [pageSiteId, setPageSiteId] = useState<string | null>(null);
  const [pageSiteName, setPageSiteName] = useState<string | null>(null);

  const setPageSiteContext = useCallback((siteId: string | null, siteName?: string | null) => {
    setPageSiteId(siteId);
    setPageSiteName(siteName || null);
  }, []);

  const clearPageSiteContext = useCallback(() => {
    setPageSiteId(null);
    setPageSiteName(null);
  }, []);

  return (
    <PageSiteContext.Provider 
      value={{ 
        pageSiteId, 
        pageSiteName, 
        setPageSiteContext, 
        clearPageSiteContext 
      }}
    >
      {children}
    </PageSiteContext.Provider>
  );
}

/**
 * Hook to access the page site context.
 * 
 * Use this in components that need to know the current page's site context.
 */
export function usePageSiteContext(): PageSiteContextValue {
  const context = useContext(PageSiteContext);
  if (!context) {
    // Return a no-op default if used outside provider (for backward compatibility)
    return {
      pageSiteId: null,
      pageSiteName: null,
      setPageSiteContext: () => {},
      clearPageSiteContext: () => {},
    };
  }
  return context;
}

/**
 * Hook to set the page site context on mount and clear on unmount.
 * 
 * Use this in pages that have a site ID from URL params.
 * 
 * @example
 * ```tsx
 * const { siteId } = useParams();
 * useSetPageSiteContext(siteId, siteName);
 * ```
 */
export function useSetPageSiteContext(siteId: string | null | undefined, siteName?: string | null) {
  const { setPageSiteContext, clearPageSiteContext } = usePageSiteContext();
  
  React.useEffect(() => {
    if (siteId) {
      setPageSiteContext(siteId, siteName);
    }
    
    return () => {
      clearPageSiteContext();
    };
  }, [siteId, siteName, setPageSiteContext, clearPageSiteContext]);
}
